import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import type { DisciplineSlug } from "@jumpbean/shared";

export type ButtonVariant = "primary" | "secondary" | "discipline" | "ghost";
export type ButtonSize = "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Button size - both use 64px+ touch targets */
  size?: ButtonSize;
  /** Discipline color when variant is "discipline" */
  discipline?: DisciplineSlug;
  /** Content inside the button */
  children: ReactNode;
  /** Show loading state */
  isLoading?: boolean;
  /** Full width button */
  fullWidth?: boolean;
}

/**
 * Button component with playful, child-friendly styling.
 * All buttons have minimum 64px touch targets for preschool users.
 *
 * @example
 * // Primary action button (sunshine yellow)
 * <Button variant="primary" size="lg">Let's Play!</Button>
 *
 * @example
 * // Discipline-themed button
 * <Button variant="discipline" discipline="math">Start Math</Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "lg",
      discipline,
      children,
      isLoading = false,
      fullWidth = false,
      disabled,
      className = "",
      ...props
    },
    ref
  ) => {
    // Base styles shared by all variants
    const baseStyles = `
      relative
      inline-flex items-center justify-center gap-3
      font-display font-bold
      rounded-full
      cursor-pointer
      select-none
      touch-manipulation
      transition-all duration-200
      focus:outline-none focus-visible:ring-4 focus-visible:ring-fairy-400 focus-visible:ring-offset-2
      disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
      active:scale-[0.97]
    `;

    // Size styles - all meet 64px minimum touch target
    const sizeStyles = {
      md: "min-h-[64px] px-6 text-body-lg",
      lg: "min-h-[80px] px-8 text-body-xl",
    };

    // Variant-specific styles
    const variantStyles = {
      primary: `
        bg-gradient-to-b from-sunshine-400 via-sunshine-500 to-sunshine-600
        text-neutral-900
        border-[3px] border-sunshine-600
        shadow-[0_4px_0_0_theme(colors.sunshine.700),0_6px_12px_rgba(0,0,0,0.15)]
        hover:shadow-[0_6px_0_0_theme(colors.sunshine.700),0_8px_16px_rgba(0,0,0,0.2)]
        hover:-translate-y-0.5
        active:shadow-[0_2px_0_0_theme(colors.sunshine.700),0_3px_6px_rgba(0,0,0,0.1)]
        active:translate-y-0.5
      `,
      secondary: `
        bg-cloud
        text-magic-600
        border-[3px] border-magic-300
        shadow-[0_4px_0_0_theme(colors.magic.400),0_6px_12px_rgba(0,0,0,0.1)]
        hover:bg-magic-50
        hover:border-magic-400
        hover:shadow-[0_6px_0_0_theme(colors.magic.400),0_8px_16px_rgba(0,0,0,0.15)]
        hover:-translate-y-0.5
        active:shadow-[0_2px_0_0_theme(colors.magic.400),0_3px_6px_rgba(0,0,0,0.08)]
        active:translate-y-0.5
      `,
      ghost: `
        bg-transparent
        text-neutral-700
        border-[3px] border-transparent
        hover:bg-neutral-100
        hover:border-neutral-200
        active:bg-neutral-200
      `,
      discipline: "",
    };

    // Discipline-specific colors
    const disciplineStyles: Record<DisciplineSlug, string> = {
      math: `
        bg-gradient-to-b from-luna-light via-luna to-luna-dark
        text-neutral-900
        border-[3px] border-luna-dark
        shadow-[0_4px_0_0_theme(colors.luna.dark),0_6px_12px_rgba(0,0,0,0.15)]
        hover:shadow-[0_6px_0_0_theme(colors.luna.dark),0_8px_16px_rgba(0,0,0,0.2)]
        hover:-translate-y-0.5
        active:shadow-[0_2px_0_0_theme(colors.luna.dark),0_3px_6px_rgba(0,0,0,0.1)]
        active:translate-y-0.5
      `,
      spelling: `
        bg-gradient-to-b from-petal-light via-petal to-petal-dark
        text-neutral-900
        border-[3px] border-petal-dark
        shadow-[0_4px_0_0_theme(colors.petal.dark),0_6px_12px_rgba(0,0,0,0.15)]
        hover:shadow-[0_6px_0_0_theme(colors.petal.dark),0_8px_16px_rgba(0,0,0,0.2)]
        hover:-translate-y-0.5
        active:shadow-[0_2px_0_0_theme(colors.petal.dark),0_3px_6px_rgba(0,0,0,0.1)]
        active:translate-y-0.5
      `,
      patterns: `
        bg-gradient-to-r from-prism-red via-prism-yellow to-prism-blue
        text-neutral-900
        border-[3px] border-prism-red
        shadow-[0_4px_0_0_theme(colors.prism.red.dark),0_6px_12px_rgba(0,0,0,0.15)]
        hover:shadow-[0_6px_0_0_theme(colors.prism.red.dark),0_8px_16px_rgba(0,0,0,0.2)]
        hover:-translate-y-0.5
        active:shadow-[0_2px_0_0_theme(colors.prism.red.dark),0_3px_6px_rgba(0,0,0,0.1)]
        active:translate-y-0.5
      `,
      typing: `
        bg-gradient-to-b from-whiskers-light via-whiskers to-whiskers-dark
        text-neutral-900
        border-[3px] border-whiskers-dark
        shadow-[0_4px_0_0_theme(colors.whiskers.dark),0_6px_12px_rgba(0,0,0,0.15)]
        hover:shadow-[0_6px_0_0_theme(colors.whiskers.dark),0_8px_16px_rgba(0,0,0,0.2)]
        hover:-translate-y-0.5
        active:shadow-[0_2px_0_0_theme(colors.whiskers.dark),0_3px_6px_rgba(0,0,0,0.1)]
        active:translate-y-0.5
      `,
      programming: `
        bg-gradient-to-b from-pixel-light via-pixel to-pixel-dark
        text-neutral-900
        border-[3px] border-pixel-dark
        shadow-[0_4px_0_0_theme(colors.pixel.dark),0_6px_12px_rgba(0,0,0,0.15)]
        hover:shadow-[0_6px_0_0_theme(colors.pixel.dark),0_8px_16px_rgba(0,0,0,0.2)]
        hover:-translate-y-0.5
        active:shadow-[0_2px_0_0_theme(colors.pixel.dark),0_3px_6px_rgba(0,0,0,0.1)]
        active:translate-y-0.5
      `,
    };

    const getVariantStyle = () => {
      if (variant === "discipline" && discipline) {
        return disciplineStyles[discipline];
      }
      return variantStyles[variant];
    };

    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={`
          ${baseStyles}
          ${sizeStyles[size]}
          ${getVariantStyle()}
          ${fullWidth ? "w-full" : ""}
          ${className}
        `}
        {...props}
      >
        {isLoading ? (
          <>
            <LoadingSpinner />
            <span className="opacity-70">{children}</span>
          </>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = "Button";

/** Small loading spinner for button loading state */
function LoadingSpinner() {
  return (
    <svg
      className="animate-spin h-5 w-5"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export default Button;
